/**
 * PageEditor — BlockNote editor wrapper for page editing.
 *
 * Initializes BlockNote with extensions, loads page content,
 * handles save (Ctrl+S and Save button), and displays unsaved changes indicator.
 * Includes WikiLink autocomplete and image upload support.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateBlockNote,
  BlockNoteViewRaw,
} from '@blocknote/react';
import type { BlockNoteEditor, PartialBlock } from '@blocknote/core';
import { usePagesContext } from '../../state/pages-context';
import { EditorToolbar } from './EditorToolbar';
import { TableToolbar } from './TableToolbar';
import { MediaBlockOverlay } from './MediaBlockOverlay';
import { WikiLinkAutocomplete, useWikiLinkTrigger } from './WikiLinkAutocomplete';
import { MentionAutocomplete, useMentionTrigger } from './MentionAutocomplete';
import { MermaidRenderer } from './MermaidRenderer';
import { resolveAttachmentUrls, migrateToStablePaths } from './attachment-url-fixer';
import { useEditLock } from './useEditLock';
import { getTokens } from '../../services/auth-client';
import { decodeJwtPayload } from '../../services/jwt-utils';
import type { LockEvent } from './usePageSubscription';
import * as pagesApi from '../../api/pages-api';

// BlockNote CSS is handled via globals.css @import

interface PageEditorProps {
  readonly pageId: string;
  readonly libraryId: string;
  /** Lock event from WebSocket subscription (passed from PagesRoute) */
  readonly lockEvent?: LockEvent | null;
  /** If true, the editor is in read-only mode (user has viewer access only) */
  readonly readOnly?: boolean;
}

/**
 * Upload handler for images pasted/dropped into the editor.
 * Calls the BFF attachments endpoint to get a presigned URL,
 * uploads the file to S3, then returns the stable BFF redirect path
 * for the image node (never expires, auth-enforced per request).
 */
async function uploadFile(pageId: string, file: File): Promise<string> {
  // Request presigned upload URL from BFF
  const attachment = await pagesApi.createAttachment(pageId, {
    filename: file.name,
    mimeType: file.type,
    size: file.size,
  });

  // Upload directly to S3 via presigned PUT URL
  if (attachment.uploadUrl) {
    await fetch(attachment.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
  }

  // Return stable BFF redirect path — never expires, auth checked on each request
  return `/api/v1/pages/${encodeURIComponent(pageId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download?redirect=true`;
}

export function PageEditor({ pageId, libraryId, lockEvent, readOnly }: PageEditorProps): React.ReactElement {
  const { state, savePage, setHasUnsavedChanges } = usePagesContext();
  const [saving, setSaving] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const initialContentRef = useRef<PartialBlock[] | undefined>(undefined);

  // Extract current user identity from JWT tokens
  const currentUserId = useMemo(() => {
    const tokens = getTokens();
    if (!tokens?.accessToken) return '';
    const claims = decodeJwtPayload(tokens.accessToken);
    return claims?.sub ?? '';
  }, []);

  const displayName = useMemo(() => {
    const tokens = getTokens();
    if (!tokens?.idToken) return 'Unknown';
    const claims = decodeJwtPayload(tokens.idToken);
    return claims?.name ?? claims?.email ?? 'Unknown';
  }, []);

  // Edit lock management
  const editLock = useEditLock({ pageId, userId: currentUserId, displayName });

  // Bridge WebSocket lock events from PagesRoute to the edit lock handler
  useEffect(() => {
    if (lockEvent) {
      editLock.handleLockEvent(lockEvent);
    }
  }, [lockEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse initial content from active page, fixing any legacy presigned URLs
  // Step 1: Synchronously migrate legacy S3 URLs to stable paths (for saving)
  const rawContent = useMemo(() => {
    if (!state.activePage?.content) return undefined;
    try {
      const content = state.activePage.content;
      let blocks: PartialBlock[] | undefined;
      if (Array.isArray(content) && content.length > 0) {
        blocks = content as PartialBlock[];
      } else if (typeof content === 'object' && content !== null && 'content' in content) {
        const inner = (content as Record<string, unknown>).content;
        if (Array.isArray(inner) && inner.length > 0) {
          blocks = inner as PartialBlock[];
        }
      }
      if (!blocks) return undefined;

      // Migrate any legacy S3 URLs to stable BFF paths
      const { blocks: migratedBlocks, fixed } = migrateToStablePaths(blocks);
      if (fixed) {
        // Will auto-save the stable paths on next edit
        return migratedBlocks;
      }
      return blocks;
    } catch {
      return undefined;
    }
  }, [state.activePage?.content]);

  // Step 2: Async — resolve stable paths to fresh presigned URLs for rendering
  const [initialContent, setInitialContent] = useState<PartialBlock[] | undefined>(undefined);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    if (!rawContent) {
      setInitialContent(undefined);
      setContentReady(true);
      return;
    }

    let cancelled = false;
    setContentReady(false);

    resolveAttachmentUrls(rawContent).then(({ blocks }) => {
      if (!cancelled) {
        setInitialContent(blocks);
        setContentReady(true);
      }
    }).catch(() => {
      // On failure, fall back to raw content (images may be broken)
      if (!cancelled) {
        setInitialContent(rawContent);
        setContentReady(true);
      }
    });

    return () => { cancelled = true; };
  }, [rawContent]);

  // Store initial content ref for comparison
  useEffect(() => {
    initialContentRef.current = initialContent;
  }, [initialContent]);

  // Create BlockNote editor with paste handler that fixes HTML paste.
  // BlockNote's default pasteHTML() calls tryParseHTMLToBlocks which only
  // understands BlockNote's own div[data-node-type] format, NOT standard
  // HTML (<h1>, <strong>, <ul>, etc). We override the paste handler to use
  // pasteHTML(html, raw=true) which sends HTML directly to ProseMirror's
  // native paste handling, which DOES understand standard HTML elements.
  const editor = useCreateBlockNote(
    {
      initialContent: initialContent || undefined,
      uploadFile: (file: File) => uploadFile(pageId, file),
      placeholders: {
        default: 'Start typing...',
        emptyDocument: 'Start typing...',
      },
      pasteHandler: ({ event, editor: ed, defaultPasteHandler }) => {
        // If inside a code block, paste as plain text (default handler)
        const currentBlock = ed.getTextCursorPosition().block;
        if (currentBlock?.type === 'codeBlock') {
          return defaultPasteHandler();
        }

        const types = Array.from(event.clipboardData?.types ?? []);

        // If clipboard has text/html, paste it raw (bypasses broken tryParseHTMLToBlocks)
        if (types.includes('text/html')) {
          const html = event.clipboardData!.getData('text/html');
          if (html) {
            // pasteHTML with raw=true skips the blocks round-trip and sends
            // HTML directly to ProseMirror's view.pasteHTML which correctly
            // parses <h1>, <strong>, <em>, <ul>, <table>, etc.
            ed.pasteHTML(html, true);
            return true;
          }
        }

        // For plain text, use default handler with markdown interpretation enabled
        return defaultPasteHandler({ plainTextAsMarkdown: true });
      },
    },
    [pageId, state.activePage?.pageId, contentReady]
  );

  // Keyboard navigation: consistent Enter behavior for multi-line blocks
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const cursor = editor.getTextCursorPosition();
      if (!cursor?.block) return;

      // Media blocks: Enter/ArrowDown moves past them
      const mediaTypes = ['image', 'video', 'file'];
      if ((e.key === 'Enter' || e.key === 'ArrowDown') && mediaTypes.includes(cursor.block.type)) {
        e.preventDefault();
        const blocks = editor.document;
        const idx = blocks.findIndex((b: any) => b.id === cursor.block.id);

        if (idx >= 0) {
          if (idx === blocks.length - 1) {
            editor.insertBlocks(
              [{ type: 'paragraph', content: [] } as any],
              cursor.block,
              'after'
            );
            const updatedBlocks = editor.document;
            const nextBlock = updatedBlocks[idx + 1];
            if (nextBlock) {
              editor.setTextCursorPosition(nextBlock, 'start');
            }
          } else {
            const nextBlock = blocks[idx + 1];
            editor.setTextCursorPosition(nextBlock, 'start');
          }
        }
        return;
      }

      // Blockquotes: Enter inserts a soft break (new line within block),
      // consistent with code block behavior. Exit via Backspace on empty line.
      if (e.key === 'Enter' && !e.shiftKey && cursor.block.type === 'quote') {
        // Check if the current line is empty — if so, exit the quote (default behavior)
        const content = cursor.block.content;
        const textContent = Array.isArray(content)
          ? content.map((item: any) => item.text || '').join('')
          : '';

        if (textContent.trim() === '') {
          // Empty quote block — let default behavior exit the block
          return;
        }

        // Non-empty quote: convert Enter into a soft break (Shift+Enter equivalent)
        e.preventDefault();
        // Insert a newline character at cursor position using the underlying ProseMirror
        const pmView = (editor as any)._tiptapEditor?.view;
        if (pmView) {
          const { state, dispatch } = pmView;
          const br = state.schema.nodes.hardBreak?.create();
          if (br) {
            const tr = state.tr.replaceSelectionWith(br);
            dispatch(tr.scrollIntoView());
          }
        }
      }
    };

    // Attach to the editor's DOM element
    const editorEl = document.querySelector('.bn-editor');
    editorEl?.addEventListener('keydown', handleKeyDown as EventListener);

    return () => {
      editorEl?.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [editor]);

  // Track content changes for unsaved indicator
  useEffect(() => {
    if (!editor) return;

    const unsubscribe = editor.onChange(() => {
      // Skip edit tracking in read-only mode
      if (readOnly) return;

      // Signal edit activity for lock refresh timing
      editLock.markEditing();

      // Implicitly acquire lock on first edit if not already holding it
      if (!editLock.isLockOwner && !editLock.isLocked) {
        editLock.acquireLock();
      }

      setHasUnsavedChanges(true);

      // Ensure a trailing paragraph exists if the last block is media (image/video/file)
      // This prevents the issue where users can't click below an image to continue typing
      const blocks = editor.document;
      if (blocks.length > 0) {
        const lastBlock = blocks[blocks.length - 1];
        const mediaTypes = ['image', 'video', 'file'];
        if (mediaTypes.includes(lastBlock.type)) {
          editor.insertBlocks(
            [{ type: 'paragraph', content: [] } as any],
            lastBlock,
            'after'
          );
        }
      }
    });

    return unsubscribe;
  }, [editor, setHasUnsavedChanges, editLock]);

  // Auto-save: debounced save 2 seconds after last edit
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editor) return;

    const unsubscribe = editor.onChange(() => {
      // Don't auto-save if read-only or the page is locked by another user
      if (readOnly || editLock.isLocked) return;

      // Clear any pending auto-save timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Schedule auto-save 2 seconds after last change
      autoSaveTimerRef.current = setTimeout(() => {
        const document = editor.document;
        savePage(pageId, document).then(() => {
          setHasUnsavedChanges(false);
        }).catch(() => {
          // Error handled by PagesContext (sets state.error)
        });
      }, 2000);
    });

    return () => {
      unsubscribe();
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [editor, pageId, savePage, setHasUnsavedChanges, editLock.isLocked, readOnly]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!editor || saving || readOnly) return;

    // Cancel any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    setSaving(true);
    try {
      const document = editor.document;
      await savePage(pageId, document);
      setHasUnsavedChanges(false);
    } catch {
      // Error is handled by PagesContext (sets state.error)
    } finally {
      setSaving(false);
    }
  }, [editor, pageId, savePage, saving, setHasUnsavedChanges]);

  // Ctrl+S keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // WikiLink autocomplete integration
  const { isOpen, searchText, position, close } = useWikiLinkTrigger(editor);

  const handleWikiLinkSelect = useCallback(
    (selectedPageId: string, title: string) => {
      if (!editor) return;

      // Get current block and remove the [[ trigger text
      const cursor = editor.getTextCursorPosition();
      if (!cursor || !cursor.block) {
        close();
        return;
      }

      const block = cursor.block;
      const textContent = extractBlockText(block.content);
      const triggerIndex = textContent.lastIndexOf('[[');

      if (triggerIndex !== -1) {
        // Remove the [[searchText from the current block
        const beforeTrigger = textContent.slice(0, triggerIndex);
        const afterCursor = ''; // The cursor is at the end of the search text

        // Update block content to remove trigger and insert wiki link
        const newContent: any[] = [];
        if (beforeTrigger) {
          newContent.push({ type: 'text', text: beforeTrigger, styles: {} });
        }
        newContent.push({
          type: 'wikiLink',
          props: { pageId: selectedPageId, displayText: title, libraryId, broken: 'false' },
          content: undefined,
        });
        if (afterCursor) {
          newContent.push({ type: 'text', text: afterCursor, styles: {} });
        }

        editor.updateBlock(block, { content: newContent } as any);
      }

      close();
      editor.focus();
    },
    [editor, libraryId, close]
  );

  // Mention (@) autocomplete integration
  const {
    isOpen: isMentionOpen,
    searchText: mentionSearchText,
    position: mentionPosition,
    close: closeMention,
  } = useMentionTrigger(editor);

  const handleMentionSelect = useCallback(
    (userId: string, displayName: string) => {
      if (!editor) return;

      const cursor = editor.getTextCursorPosition();
      if (!cursor || !cursor.block) {
        closeMention();
        return;
      }

      const block = cursor.block;
      const textContent = extractBlockText(block.content);

      // Find the @ trigger position
      let atIndex = -1;
      for (let i = textContent.length - 1; i >= 0; i--) {
        if (textContent[i] === '@') {
          if (i === 0 || /[\s\n([\-{]/.test(textContent[i - 1])) {
            atIndex = i;
            break;
          }
        }
      }

      if (atIndex !== -1) {
        const beforeTrigger = textContent.slice(0, atIndex);

        // Replace @searchText with a styled mention link
        const newContent: any[] = [];
        if (beforeTrigger) {
          newContent.push({ type: 'text', text: beforeTrigger, styles: {} });
        }
        newContent.push({
          type: 'link',
          href: `#user-${userId}`,
          content: [{ type: 'text', text: `@${displayName}`, styles: {} }],
        });
        // Add a trailing space after the mention
        newContent.push({ type: 'text', text: ' ', styles: {} });

        editor.updateBlock(block, { content: newContent } as any);
      }

      closeMention();
      editor.focus();
    },
    [editor, closeMention]
  );

  return (
    <div className="flex flex-col h-full">
      {!contentReady && (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">Loading page content...</p>
        </div>
      )}
      {contentReady && (
      <>
      {!readOnly && (
      <EditorToolbar
        editor={editor}
        pageId={pageId}
        hasUnsavedChanges={state.hasUnsavedChanges}
        saving={saving}
        onSave={handleSave}
      />
      )}

      {/* Read-only lock banner */}
      {editLock.isLocked && (
        <div
          className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm flex items-center justify-between"
          role="alert"
          aria-live="polite"
        >
          <p>{editLock.lockHolder} is currently editing this page.</p>
          <button
            type="button"
            onClick={async () => { await editLock.takeOver(); }}
            className="px-3 py-1 text-xs font-medium rounded border border-amber-300 hover:bg-amber-100 transition-colors"
          >
            Take over editing
          </button>
        </div>
      )}

      <div ref={editorContainerRef} className="flex-1 overflow-y-auto relative">
        <div className="max-w-4xl mx-auto py-6 px-4">
          <BlockNoteViewRaw
            editor={editor}
            theme="dark"
            editable={!readOnly && !editLock.isLocked}
            formattingToolbar={false}
            sideMenu={!readOnly}
            filePanel={false}
          />
        </div>

        {/* Mermaid diagram previews */}
        <div className="max-w-4xl mx-auto px-4 pb-6">
          <MermaidRenderer editor={editor} />
        </div>

        {/* Table hover toolbar */}
        <TableToolbar editor={editor} containerRef={editorContainerRef} />

        {/* Media block hover overlay (download, delete, insert-below) */}
        <MediaBlockOverlay editor={editor} containerRef={editorContainerRef} pageId={pageId} />

        {/* Wiki link autocomplete dropdown */}
        {isOpen && (
          <WikiLinkAutocomplete
            editor={editor}
            libraryId={libraryId}
            position={position}
            searchText={searchText}
            onSelect={handleWikiLinkSelect}
            onClose={close}
          />
        )}

        {/* Mention (@) autocomplete dropdown */}
        {isMentionOpen && (
          <MentionAutocomplete
            editor={editor}
            libraryId={libraryId}
            position={mentionPosition}
            searchText={mentionSearchText}
            onSelect={handleMentionSelect}
            onClose={closeMention}
          />
        )}
      </div>
      </>
      )}
    </div>
  );
}

/**
 * Extract plain text from BlockNote block content array.
 */
function extractBlockText(content: unknown): string {
  if (!content || !Array.isArray(content)) return '';
  return content
    .map((item: any) => {
      if (item.type === 'text') return item.text || '';
      if (item.type === 'link') {
        return (item.content || []).map((c: any) => c.text || '').join('');
      }
      return '';
    })
    .join('');
}
